import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { Card, Select, Row, Col, Button, Statistic, Typography, DatePicker, Space, Tag } from 'antd';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import axios from 'axios';
import { BackendAPI } from '../../axios';
import { DatabaseOutlined, CalendarOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { TransactionData, getUniqueYears, getDateRange } from '../AuthComponents/dammyData';

import "./TransactionDashBoard.scss";
import ServerStatus from './ServerStatus';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const THEME_COLOR = '#1515b4';

const COLORS = {
    primary: THEME_COLOR,
    secondary: "#245a96",   // OTP - darker blue
    accent1: "#8d0da6",     // BFD
    accent2: "#7a238f",     // AUTH - darker purple
    accent3: "#3a3aef",
    production: "#237804",
    preproduction: "#ad6800",
};

const TX_TYPE_COLORS: Record<string, string> = {
    "e-KYC": COLORS.primary,
    OTP: COLORS.secondary,
    BFD: COLORS.accent1,
    AUTH: COLORS.accent2,
};
interface Props {
setIsOpenTxnDashboard?: React.Dispatch<React.SetStateAction<boolean | null>>;
}

const TransactionDashboard: React.FC<Props> = ({ setIsOpenTxnDashboard }) => {
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('all');
  const [selectedTxnTypes, setSelectedTxnTypes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [monthRange, setMonthRange] = useState<number>(12); // Default: Last 12 months
  const [transactionData, setTransactionData] = useState<TransactionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const location = useLocation();
  const [serverStatusVisible, setServerStatusVisible] = useState(false);

  let refreshToken: null;


  // Extract available years
  const availableYears = useMemo(() => getUniqueYears(transactionData), [transactionData]);

  // Get overall date range
  const overallDateRange = useMemo(() => getDateRange(transactionData), [transactionData]);

  // Get available transaction types
  const availableTxnTypes = useMemo(() => {
    return Array.from(new Set(transactionData.map((item) => item.txnType)));
  }, [transactionData]);

  // Get available environments
  const availableEnvironments = useMemo(() => {
    return Array.from(new Set(transactionData.map((item) => item.environment)));
  }, [transactionData]);

  // Filter data based on all filters
  const filteredData = useMemo(() => {
    let filtered = transactionData;

    // Filter by year
    if (selectedYear !== 'all') {
      filtered = filtered.filter((item) => item.yearmonth.startsWith(selectedYear));
    }

    // Filter by environment
    if (selectedEnvironment !== 'all') {
      filtered = filtered.filter((item) => item.environment === selectedEnvironment);
    }

    // Filter by transaction types
    if (selectedTxnTypes.length > 0) {
      filtered = filtered.filter((item) => selectedTxnTypes.includes(item.txnType));
    }

    // Filter by date range (custom date range)
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].format('YYYY-MM');
      const endDate = dateRange[1].format('YYYY-MM');
      filtered = filtered.filter((item) => {
        return item.yearmonth >= startDate && item.yearmonth <= endDate;
      });
    } else if (!dateRange) {
      // Filter by month range (last N months) - only if no custom date range
      const allMonths = Array.from(new Set(filtered.map((item) => item.yearmonth))).sort();
      if (allMonths.length > monthRange) {
        const recentMonths = allMonths.slice(-monthRange);
        filtered = filtered.filter((item) => recentMonths.includes(item.yearmonth));
      }
    }

    return filtered;
  }, [selectedYear, selectedEnvironment, selectedTxnTypes, dateRange, monthRange, transactionData]);

  // Get filtered date range for display
  const filteredDateRange = useMemo(() => {
    if (filteredData.length === 0) return { start: 'N/A', end: 'N/A' };
    const dates = filteredData.map((item) => item.yearmonth).sort();
    return {
      start: dates[0],
      end: dates[dates.length - 1],
    };
  }, [filteredData]);

  // Prepare data for charts
  const monthlyTrends = useMemo(() => {
    const grouped = filteredData.reduce((acc, curr) => {
      const existing = acc.find((item) => item.month === curr.yearmonth);
      if (existing) {
        existing.total += curr.txncount;
      } else {
        acc.push({ month: curr.yearmonth, total: curr.txncount });
      }
      return acc;
    }, [] as { month: string; total: number }[]);
    return grouped.sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredData]);

  const transactionByType = useMemo(() => {
    const grouped = filteredData.reduce((acc, curr) => {
      const existing = acc.find((item) => item.type === curr.txnType);
      if (existing) {
        existing.count += curr.txncount;
      } else {
        acc.push({ type: curr.txnType, count: curr.txncount });
      }
      return acc;
    }, [] as { type: string; count: number }[]);
    return grouped;
  }, [filteredData]);

  const environmentData = useMemo(() => {
    const grouped = filteredData.reduce((acc, curr) => {
      const existing = acc.find((item) => item.environment === curr.environment);
      if (existing) {
        existing.count += curr.txncount;
      } else {
        acc.push({ environment: curr.environment, count: curr.txncount });
      }
      return acc;
    }, [] as { environment: string; count: number }[]);
    return grouped;
  }, [filteredData]);

  const typeByMonthData = useMemo(() => {
    const months = Array.from(new Set(filteredData.map((item) => item.yearmonth))).sort();
    return months.map((month) => {
      const monthData = filteredData.filter((item) => item.yearmonth === month);
      const result: any = { month };
      monthData.forEach((item) => {
        result[item.txnType] = (result[item.txnType] || 0) + item.txncount;
      });
      return result;
    });
  }, [filteredData]);

  const totalTransactions = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + item.txncount, 0);
  }, [filteredData]);

  const totalTypes = useMemo(() => {
    return new Set(filteredData.map((item) => item.txnType)).size;
  }, [filteredData]);

  const handleResetFilters = () => {
    setSelectedYear('all');
    setSelectedEnvironment('all');
    setSelectedTxnTypes([]);
    setDateRange(null);
    setMonthRange(12);
  };
  const getRefreshToken = async () => {
    try {
      const response = await BackendAPI.get("/user/refresh-token", { withCredentials: true });
      refreshToken = response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.request.status == 401) {
          window.location.reload();
        }
      }
    }
  };

  const getTransactionSummary = async () => {
    const url = `/txns/reg_id`;
    const header = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refreshToken}`,
      },
    };

    try {
      const response = await BackendAPI.get(url, header);
      setTransactionData(response.data);
      console.log("data is => ", response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.request.status == 401) {
          // handleLogout();
        }
      }
    }
  };
  useEffect(() => {
    const executeFunctions = async () => {
      await getRefreshToken();
      await getTransactionSummary();
    };
    executeFunctions();
  }, []);

  const openIntegrationStatus = () => {
    setIsOpenTxnDashboard?.(false);
  }
const formatIndianNumber = (num: number) => {
  const format = (value: number, suffix: string) => {
    const formatted = value.toFixed(1);
    return (formatted.endsWith('.0') ? parseInt(formatted).toString() : formatted) + suffix;
  };

  if (num >= 10000000) return format(num / 10000000, ' Cr');
  if (num >= 100000) return format(num / 100000, ' L');
  if (num >= 1000) return format(num / 1000, ' K');
  return num.toString();
};
  return (
    <div
      style={{
        padding: '12px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        minHeight: '100vh',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
<div
    style={{
        marginBottom: "12px",
        padding: "24px",
        background: `linear-gradient(
            135deg,
            ${THEME_COLOR} 0%,
            ${COLORS.secondary} 100%
        )`,
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(21, 21, 180, 0.15)",

        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
        flexWrap: "wrap",
    }}
>
    {/* Header Title */}
    <div>
        <Title
            level={2}
            style={{
                color: "#fff",
                margin: 0,
                marginBottom: "6px",
            }}
        >
            Transaction Analytics Dashboard
        </Title>

        {/* 
        <Text
            style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "14px",
            }}
        >
            <CalendarOutlined />
            Overall Data Period: {overallDateRange.start} to{" "}
            {overallDateRange.end}
        </Text>
        */}
    </div>
     {/* Header Buttons */}
{location.pathname !== "/user/txnsummary" && (
    <Space size="middle" wrap>
        {/* Server Status */}
        <Button
            type="primary"
            onClick={() => setServerStatusVisible(true)}
            style={{
                background: "#ffffff",
                color: THEME_COLOR,
                border: "none",
                borderRadius: "8px",
                height: "42px",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: 600,
                boxShadow: "0 3px 10px rgba(0, 0, 0, 0.15)",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f0f5ff";
                e.currentTarget.style.color = THEME_COLOR;
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(0, 0, 0, 0.20)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.color = THEME_COLOR;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                    "0 3px 10px rgba(0, 0, 0, 0.15)";
            }}
        >
           UIDAI Server Status
        </Button>

        {/* Integration Status */}
        <Button
            type="primary"
            onClick={openIntegrationStatus}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f0f5ff";
                e.currentTarget.style.color = THEME_COLOR;
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(0, 0, 0, 0.20)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.color = THEME_COLOR;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                    "0 3px 10px rgba(0, 0, 0, 0.15)";
            }}
            style={{
                background: "#ffffff",
                color: THEME_COLOR,
                border: "none",
                borderRadius: "8px",
                height: "42px",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: 600,
                boxShadow: "0 3px 10px rgba(0, 0, 0, 0.15)",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                whiteSpace: "nowrap",
            }}
        >
            View Integration Status
        </Button>
    </Space>
)}
   </div>

        {/* Filter Section */}
        <Card styles={{
          body: {
            padding: !filtersOpen ? "0px" : "",
            transition: "all 0.5s ease"
          }
        }}
          title={
            <div style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span
                style={{ color: THEME_COLOR, fontWeight: 600, cursor: "pointer" }}
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <FilterOutlined style={{ marginRight: "8px" }} />
                Filters
                <span style={{ marginLeft: 8 }}>
                  {filtersOpen ? <UpOutlined /> : <DownOutlined />}
                </span>


              </span>
              <div>
                <Text strong style={{
                  color: THEME_COLOR, marginTop: '16px',
                  padding: '12px',
                  background: '#f0f5ff',
                  borderRadius: '4px',
                  borderLeft: `4px solid ${THEME_COLOR}`,
                }}>
                  Currently Showing Data From: {filteredDateRange.start} to {filteredDateRange.end}
                </Text>
                <Text style={{ marginLeft: '16px', color: '#666' }}>
                  ({monthlyTrends.length} months)
                </Text>
              </div>
            </div>
          }
          style={{
            marginBottom: "12px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
          extra={
            filtersOpen && (
              <a onClick={handleResetFilters} style={{ color: THEME_COLOR, marginLeft: '12px' }}>
                Reset All
              </a>
            )
          }
        >
          <div
            style={{
              maxHeight: filtersOpen ? "800px" : "0px",
              overflow: "hidden",
              transition: "max-height 0.4s ease",
            }}
          >
            <Row gutter={[16, 16]}>
              {/* Year Filter */}
             <Col xs={24} sm={12} md={6}>
    <label
        htmlFor="year-select"
        style={{
            display: "block",
            marginBottom: "8px",
            color: "#595959",
            fontWeight: 600,
        }}
    >
        Year
    </label>

    <Select
        id="year-select"
        value={selectedYear}
        onChange={setSelectedYear}
        style={{ width: "100%" }}
        size="large"
    >
        <Option value="all">All Years</Option>

        {availableYears.map((year) => (
            <Option key={year} value={year}>
                {year}
            </Option>
        ))}
    </Select>
</Col>
              {/* Environment Filter */}
             {/* Environment Filter */}
<Col xs={24} sm={12} md={6}>
    <label
        htmlFor="environment-select"
        style={{
            display: "block",
            marginBottom: "8px",
            color: "#666",
            fontWeight: 600,
        }}
    >
        Environment
    </label>

    <Select
        id="environment-select"
        value={selectedEnvironment}
        onChange={setSelectedEnvironment}
        style={{ width: "100%" }}
        size="large"
    >
        <Option value="all">All Environments</Option>

        {availableEnvironments.map((env) => (
            <Option key={env} value={env}>
                {env}
            </Option>
        ))}
    </Select>
</Col>


{/* Transaction Type Filter */}
<Col xs={24} sm={12} md={6}>
    <label
        htmlFor="transaction-type-select"
        style={{
            display: "block",
            marginBottom: "8px",
            color: "#595959",
            fontWeight: 600,
        }}
    >
        Transaction Type
    </label>

    <Select
    id="transaction-type-select"
    mode="multiple"
    showSearch={false}
    value={selectedTxnTypes}
    onChange={setSelectedTxnTypes}
    style={{ width: "100%" }}
    size="large"
    placeholder="All Types"
    allowClear
>
    {availableTxnTypes.map((type) => (
        <Option key={type} value={type}>
            {type}
        </Option>
    ))}
</Select>
</Col>

{/* Month Range Filter */}
<Col xs={24} sm={12} md={6}>
    <label
        htmlFor="month-range-select"
        style={{
            display: "block",
            marginBottom: "8px",
            color: "#666",
            fontWeight: 600,
        }}
    >
        Show Last N Months
    </label>

    <Select
        id="month-range-select"
        value={monthRange}
        onChange={(value) => {
            setMonthRange(value);
            setDateRange(null);
        }}
        style={{ width: "100%" }}
        size="large"
        disabled={!!dateRange}
    >
        <Option value={3}>Last 3 Months</Option>
        <Option value={6}>Last 6 Months</Option>
        <Option value={12}>Last 12 Months</Option>
        <Option value={24}>Last 24 Months</Option>
        <Option value={36}>Last 36 Months</Option>
        <Option value={48}>Last 48 Months</Option>
        <Option value={1000}>All Data</Option>
    </Select>
</Col>


{/* Custom Date Range */}
<Col xs={24} md={12}>
    <label
        htmlFor="custom-date-range"
        style={{
            display: "block",
            marginBottom: "8px",
            color: "#666",
            fontWeight: 600,
        }}
    >
        Custom Date Range (Optional)
    </label>

    <RangePicker
        id="custom-date-range"
        value={dateRange}
        onChange={(dates) => setDateRange(dates)}
        picker="month"
        format="YYYY-MM"
        style={{ width: "100%" }}
        size="large"
    />
</Col>

              {/* Active Filters Display */}
              <Col xs={24}>
                <Space size={[8, 8]} wrap style={{ marginTop: '8px' }}>
                  <Text strong style={{ color: '#666' }}>
                    Active Filters:
                  </Text>
                  {selectedYear !== 'all' && (
                    <Tag color={THEME_COLOR} closable onClose={() => setSelectedYear('all')}>
                      Year: {selectedYear}
                    </Tag>
                  )}
                  {selectedEnvironment !== 'all' && (
                    <Tag color={THEME_COLOR} closable onClose={() => setSelectedEnvironment('all')}>
                      Env: {selectedEnvironment}
                    </Tag>
                  )}
                  {selectedTxnTypes.map((type) => (
                    <Tag
                      key={type}
                      color={THEME_COLOR}
                      closable
                      onClose={() => setSelectedTxnTypes(selectedTxnTypes.filter((t) => t !== type))}
                    >
                      Type: {type}
                    </Tag>
                  ))}
                  {dateRange && (
                    <Tag color={THEME_COLOR} closable onClose={() => setDateRange(null)}>
                      Date: {dateRange[0]?.format('YYYY-MM')} to {dateRange[1]?.format('YYYY-MM')}
                    </Tag>
                  )}
                  {!dateRange && monthRange !== 12 && (
                    <Tag color={THEME_COLOR} closable onClose={() => setMonthRange(12)}>
                      Range: Last {monthRange} months
                    </Tag>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

        </Card>

        {/* Summary Statistics */}
     <Row
  gutter={[16, 16]}
  style={{
    marginBottom: "12px",
    display: "flex",
    alignItems: "stretch",
  }}
>
  {/* Services Availed */}
  <Col xs={24} sm={12} lg={6} style={{ display: "flex" }}>
    <Card
      bordered={false}
      style={{
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
      }}
      styles={{
        body: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
      }}
    >
      <div>
        <div
          style={{
            fontSize: 14,
            color: "#595959",
            marginBottom: "12px",
            fontWeight: 500,
          }}
        >
          Services Availed
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {Array.from(
            new Set(filteredData.map((item) => item.txnType))
          ).map((type) => (
            <Tag
              key={type}
              color={TX_TYPE_COLORS[type] || COLORS.accent3}
              style={{
                margin: 0,
                fontSize: 12,
                padding: "4px 10px",
                fontWeight: 500,
              }}
            >
              {type}
            </Tag>
          ))}
        </div>
      </div>
    </Card>
  </Col>

  {/* Total Transactions */}
  <Col xs={24} sm={12} lg={6} style={{ display: "flex" }}>
    <Card
      bordered={false}
      style={{
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
      }}
      styles={{
        body: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <Statistic
        title={
          <span style={{ color: "#595959", fontWeight: 500 }}>
            Total Transactions
          </span>
        }
        value={totalTransactions}
        prefix={
          <DatabaseOutlined
            style={{ color: "#0958d9" }}
          />
        }
        valueStyle={{
          color: "#0958d9",
          fontWeight: 600,
        }}
      />
    </Card>
  </Col>

  {/* Production */}
  <Col xs={24} sm={12} lg={6} style={{ display: "flex" }}>
    <Card
      bordered={false}
      style={{
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
      }}
      styles={{
        body: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <Statistic
        title={
          <span style={{ color: "#595959", fontWeight: 500 }}>
            Production
          </span>
        }
        value={
          environmentData.find(
            (e) => e.environment === "Production"
          )?.count || 0
        }
        valueStyle={{
          color: "#237804",
          fontWeight: 600,
        }}
      />
    </Card>
  </Col>

  {/* Preproduction */}
  <Col xs={24} sm={12} lg={6} style={{ display: "flex" }}>
    <Card
      bordered={false}
      style={{
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
      }}
      styles={{
        body: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <Statistic
        title={
          <span style={{ color: "#595959", fontWeight: 500 }}>
            Preproduction
          </span>
        }
        value={
          environmentData.find(
            (e) => e.environment === "Preproduction"
          )?.count || 0
        }
        valueStyle={{
          color: "#ad6800",
          fontWeight: 600,
        }}
      />
    </Card>
  </Col>
</Row>
        {/* Charts */}
        <Row gutter={[16, 16]} style={{rowGap: '12px', }}>
          {/* Monthly Trends */}
          <Col xs={24} lg={12}>
            <Card
              title={<span style={{ color: THEME_COLOR, fontWeight: 600 }}>Monthly Transaction Trends ({filteredDateRange.start} to {filteredDateRange.end})</span>}
              bordered={false}
              style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="month"
                    stroke="#666"
                    style={{ fontSize: '12px' }}
                    angle={monthlyTrends.length > 12 ? -45 : 0}
                    textAnchor={monthlyTrends.length > 12 ? 'end' : 'middle'}
                    height={monthlyTrends.length > 12 ? 80 : 30}
                  />
                  <YAxis stroke="#666" style={{ fontSize: '12px' }} 
                    tickFormatter={formatIndianNumber}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${THEME_COLOR}`,
                      borderRadius: '4px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke={THEME_COLOR}
                    strokeWidth={3}
                    dot={{ fill: THEME_COLOR, r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Transaction by Type - Pie Chart */}
          <Col xs={24} lg={12}>
            <Card
              title={<span style={{ color: THEME_COLOR, fontWeight: 600 }}>Transactions by Type ({filteredDateRange.start} to {filteredDateRange.end})</span>}
              bordered={false}
              style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={transactionByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="type"
                  >
                    {transactionByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={TX_TYPE_COLORS[entry.type] || COLORS.accent3} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${THEME_COLOR}`,
                      borderRadius: '4px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Transaction Types by Month - Stacked Bar */}
          <Col xs={24}>
            <Card
              title={
                <span style={{ color: THEME_COLOR, fontWeight: 600 }}>
                  Transaction Types by Month for Year ({filteredDateRange.start} to {filteredDateRange.end})
                  <Text style={{ marginLeft: '12px', fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                    (Showing {typeByMonthData.length} months)
                  </Text>
                </span>
              }
              bordered={false}
              style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={typeByMonthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="month"
                    stroke="#666"
                    style={{ fontSize: '11px' }}
                    angle={typeByMonthData.length > 12 ? -45 : 0}
                    textAnchor={typeByMonthData.length > 12 ? 'end' : 'middle'}
                    height={typeByMonthData.length > 12 ? 80 : 30}
                  />
                  <YAxis type='number' stroke="#666" style={{ fontSize: '12px' }} 
                    tickFormatter={formatIndianNumber}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${THEME_COLOR}`,
                      borderRadius: '4px',
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  {availableTxnTypes.map((type) => (
                    <Bar
                      key={type}
                      dataKey={type}
                      stackId="a"
                      fill={TX_TYPE_COLORS[type] || COLORS.accent3}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Environment Comparison */}
          <Col xs={24} lg={12}>
            <Card
              title={<span style={{ color: THEME_COLOR, fontWeight: 600 }}>Environment Distribution ({filteredDateRange.start} to {filteredDateRange.end})</span>}
              bordered={false}
              style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={environmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="environment" stroke="#666" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#666" style={{ fontSize: '12px' }} 
                  tickFormatter={formatIndianNumber}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${THEME_COLOR}`,
                      borderRadius: '4px',
                    }}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {environmentData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.environment === 'Production' ? COLORS.production : COLORS.preproduction}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Transaction Type Details */}
          <Col xs={24} lg={12}>
            <Card
              title={<span style={{ color: THEME_COLOR, fontWeight: 600 }}>Transaction Type Breakdown ({filteredDateRange.start} to {filteredDateRange.end})</span>}
              bordered={false}
              style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={transactionByType} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" stroke="#666" style={{ fontSize: '12px' }} 
                   tickFormatter={formatIndianNumber}
                  />
                  <YAxis type="category" dataKey="type" stroke="#666" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${THEME_COLOR}`,
                      borderRadius: '4px',
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                    {transactionByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={TX_TYPE_COLORS[entry.type] || COLORS.accent3} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
        <ServerStatus
    visible={serverStatusVisible}
    onClose={() => setServerStatusVisible(false)}
/>
      </div>
    </div>
  );
};

export default TransactionDashboard;